import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Bot, Play, Send, Trash2, X } from 'lucide-react'
import { KiArbeitet } from '@/components/ki/KiArbeitet'
import { erfuelltRecht } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { laufFehlerText } from '@/lib/lauf-fehler'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import { fetchAssistent, fetchKiEinstellungen, frageAssistent, leereAssistent, type AssistentAktion, type AssistentEintrag, type AssistentVerlauf, type KiEinstellungen } from '../api/calculation-assistant.api'
import { FundstelleDialog } from './FundstelleDialog'
import { KiLaufVorschlaege } from './KiLaufVorschlaege'

const BREITE_SCHLUESSEL = 'kalkulation-assistent-breite'
const BREITE_VORGABE = 420
const liesBreite = () => {
  try {
    const w = Number(localStorage.getItem(BREITE_SCHLUESSEL))
    return Number.isFinite(w) && w >= 320 && w <= 900 ? w : BREITE_VORGABE
  } catch {
    return BREITE_VORGABE
  }
}

/**
 * AP-37 Detailblatt N 8. (N8) — **Assistent** als Seitenleiste rechts in der Kalkulation: je Benutzer
 * und Kalkulation ein Verlauf. Er antwortet mit Zahlen aus den Werkzeugen und legt Änderungen als
 * **Vorschläge** an, die hier mit Häkchen übernommen werden — er selbst schreibt nie. Die Antwort
 * entsteht im Hintergrund; die Leiste fragt jede Sekunde nach, solange sie läuft (KI-19).
 * Breite merkt sich der Browser.
 */
export function AssistentLeiste({
  kalkulationId,
  beschreibbar,
  onClose,
  onGeaendert,
  onNeueVorschlaege,
  onPosition,
  onAuftrag,
  onBreite,
}: {
  kalkulationId: string
  beschreibbar: boolean
  onClose: () => void
  /** nach Übernehmen oder Verwerfen — die Kalkulation hat sich geändert */
  onGeaendert: () => void
  /** eine Antwort hat Vorschläge angelegt — nur die Zahl offener Vorschläge ändert sich */
  onNeueVorschlaege: () => void
  onPosition: (id: string) => void
  onAuftrag: (faehigkeit: string) => void
  /** damit die Seite auf breiten Schirmen Platz macht, statt verdeckt zu werden */
  onBreite?: (breite: number) => void
}) {
  const { user } = useAuthStore()
  const darfEinstellen = erfuelltRecht('calculation.settings.manage', user?.permissions)
  const [breite, setBreite] = useState(liesBreite)
  const [verlauf, setVerlauf] = useState<AssistentVerlauf | null>(null)
  const [einst, setEinst] = useState<KiEinstellungen | null>(null)
  const [text, setText] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [fundstelle, setFundstelle] = useState<{ fileId: string; seite: number } | null>(null)
  const unten = useRef<HTMLDivElement>(null)
  useEffect(() => {
    onBreite?.(breite)
  }, [breite, onBreite])

  const laden = useCallback(() => {
    void fetchAssistent(kalkulationId)
      .then(setVerlauf)
      .catch((e) => setFehler(apiErrorText(e, 'Der Verlauf ließ sich nicht laden.')))
  }, [kalkulationId])
  useEffect(laden, [laden])
  useEffect(() => {
    void fetchKiEinstellungen().then(setEinst).catch(() => setEinst(null))
  }, [])

  // Solange die Antwort entsteht, jede Sekunde nachfragen.
  const laeuft = verlauf?.status === 'RUNNING'
  useEffect(() => {
    if (!laeuft) return
    const t = setInterval(() => {
      void fetchAssistent(kalkulationId)
        .then((v) => {
          setVerlauf(v)
          if (v.status !== 'RUNNING' && v.messages.at(-1)?.runId) onNeueVorschlaege()
        })
        .catch(() => undefined)
    }, 1000)
    return () => clearInterval(t)
  }, [laeuft, kalkulationId, onNeueVorschlaege])
  const anzahl = verlauf?.messages.length ?? 0
  useEffect(() => {
    unten.current?.scrollIntoView({ block: 'end' })
  }, [anzahl, laeuft])

  async function senden() {
    const frage = text.trim()
    if (!frage || laeuft) return
    setFehler(null)
    try {
      setVerlauf(await frageAssistent(kalkulationId, frage))
      setText('')
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Frage ließ sich nicht stellen.'))
    }
  }

  async function leeren() {
    if (!(await bestaetige('Verlauf dieser Kalkulation leeren? Übernommene Vorschläge bleiben in der Kalkulation.', { ok: 'Leeren' }))) return
    try {
      setVerlauf(await leereAssistent(kalkulationId))
    } catch (e) {
      setFehler(apiErrorText(e, 'Der Verlauf ließ sich nicht leeren.'))
    }
  }

  function ziehen(e: React.MouseEvent) {
    e.preventDefault()
    const start = e.clientX
    const alt = breite
    let jetzt = alt
    const bewege = (m: MouseEvent) => {
      jetzt = Math.min(900, Math.max(320, alt + start - m.clientX))
      setBreite(jetzt)
    }
    const los = () => {
      document.removeEventListener('mousemove', bewege)
      document.removeEventListener('mouseup', los)
      try {
        localStorage.setItem(BREITE_SCHLUESSEL, String(jetzt))
      } catch {
        // Browser ohne Speicher — dann gilt die Vorgabe beim nächsten Mal
      }
    }
    document.addEventListener('mousemove', bewege)
    document.addEventListener('mouseup', los)
  }

  // Warum der Assistent nicht antworten kann — bevor jemand tippt (15.: mit Hinweis für den, der es einstellen darf).
  const aus = einst && (!einst.assistantEnabled || !einst.assistantConfirmedAt)
  const ohneSchluessel = einst && !aus && einst.assistantKeySource === 'TENANT' && !einst.tenantKey
  // Über den Schlüssel von Symphenia: das Monatslimit des Mandanten aus der Betreiber-Konsole (30.9.2026).
  const ohneKontingent = einst && !aus && einst.assistantKeySource === 'CENTRAL' && einst.quota.exhausted
  const sperre = aus || ohneSchluessel || ohneKontingent
  const zahl = (n: number) => n.toLocaleString('de-DE')

  return (
    <aside
      className="fixed bottom-0 right-0 top-0 z-40 flex flex-col border-l border-border shadow-xl"
      style={{ width: breite, maxWidth: '100vw', background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
      aria-label="Assistent"
    >
      <div className="absolute bottom-0 left-0 top-0 w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={ziehen} />
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2 font-medium">
          <Bot className="h-5 w-5 text-primary" /> Assistent
        </div>
        <div className="flex items-center gap-1">
          {anzahl > 0 && (
            <button type="button" className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" onClick={() => void leeren()} title="Verlauf leeren">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button type="button" className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" onClick={onClose} aria-label="Schließen">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 text-sm">
        {anzahl === 0 && !sperre && (
          <div className="space-y-2 text-muted-foreground">
            <p>Frag nach Zahlen dieser Kalkulation oder gib einen Auftrag — Änderungen kommen als Vorschläge, die du übernimmst.</p>
            <ul className="list-disc space-y-0.5 pl-5 text-xs">
              <li>Welche Positionen haben noch keinen Preis?</li>
              <li>Was bringen 2 % weniger Gewinn?</li>
              <li>10 % Verschnitt auf alle Rohre</li>
              <li>Steht in der Baubeschreibung etwas zur Beschichtung?</li>
            </ul>
            {!beschreibbar && <p className="text-xs">Diese Fassung ist abgegeben — nur Fragen möglich.</p>}
          </div>
        )}
        {verlauf?.messages.map((m, i) => (
          <Eintrag
            key={`${m.at}-${i}`}
            eintrag={m}
            kalkulationId={kalkulationId}
            beschreibbar={beschreibbar}
            onGeaendert={onGeaendert}
            onPosition={onPosition}
            onFundstelle={setFundstelle}
            onAktion={(a) => (a.type === 'START_TASK' && a.capability ? onAuftrag(a.capability) : undefined)}
          />
        ))}
        {laeuft && (
          <div className="text-muted-foreground">
            <KiArbeitet text="Der Assistent denkt nach" />
          </div>
        )}
        {verlauf?.status === 'FAILED' && <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive">{laufFehlerText(verlauf.error) || 'Die Antwort ist fehlgeschlagen.'}</p>}
        <div ref={unten} />
      </div>

      {sperre ? (
        <div className="border-t border-border px-4 py-3 text-sm text-muted-foreground">
          {aus
            ? 'Der Assistent ist ausgeschaltet.'
            : ohneSchluessel
              ? 'Für den Assistenten ist kein KI-Schlüssel hinterlegt. Trag einen eigenen ein oder nutze das Symphenia-Kontingent.'
              : einst.quota.limit > 0
                ? `Das Symphenia-Kontingent dieses Monats ist aufgebraucht (${zahl(einst.quota.used)} von ${zahl(einst.quota.limit)} Tokens). Mehr gibt Symphenia frei — oder du trägst einen eigenen KI-Schlüssel ein.`
                : 'Für deinen Betrieb ist kein Symphenia-Kontingent freigegeben. Trag einen eigenen KI-Schlüssel ein oder frag bei Symphenia nach einem Kontingent.'}{' '}
          {darfEinstellen ? (
            <Link to="/calculations/settings?tab=ki" className="text-primary hover:underline">
              In den KI-Einstellungen {aus ? 'einschalten' : 'ändern'}
            </Link>
          ) : (
            'Das kann einschalten, wer die Einstellungen der Kalkulation ändern darf.'
          )}
        </div>
      ) : (
        <div className="border-t border-border px-4 py-3">
          {fehler && <p className="mb-2 text-sm text-destructive">{fehler}</p>}
          <div className="flex items-end gap-2">
            <textarea
              className="min-h-[2.5rem] flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm"
              rows={Math.min(6, Math.max(1, text.split('\n').length))}
              placeholder="Frage oder Auftrag …"
              value={text}
              maxLength={4000}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  void senden()
                }
              }}
            />
            <button type="button" className="inline-flex h-10 items-center gap-1 rounded-md bg-primary px-3 text-sm text-primary-foreground disabled:opacity-50" disabled={laeuft || !text.trim()} onClick={() => void senden()}>
              <Send className="h-4 w-4" /> Fragen
            </button>
          </div>
        </div>
      )}
      {fundstelle && <FundstelleDialog kalkulationId={kalkulationId} fileId={fundstelle.fileId} seite={fundstelle.seite} onClose={() => setFundstelle(null)} />}
    </aside>
  )
}

function Eintrag({
  eintrag: m,
  kalkulationId,
  beschreibbar,
  onGeaendert,
  onPosition,
  onFundstelle,
  onAktion,
}: {
  eintrag: AssistentEintrag
  kalkulationId: string
  beschreibbar: boolean
  onGeaendert: () => void
  onPosition: (id: string) => void
  onFundstelle: (f: { fileId: string; seite: number }) => void
  onAktion: (a: AssistentAktion) => void
}) {
  if (m.role === 'user') {
    return <div className="ml-8 whitespace-pre-wrap rounded-lg bg-primary/10 px-3 py-2">{m.text}</div>
  }
  return (
    <div className="mr-4 space-y-2">
      <div className="whitespace-pre-wrap leading-relaxed">
        <MitSpruengen text={m.text} onPosition={onPosition} onFundstelle={onFundstelle} />
      </div>
      {m.runId && <KiLaufVorschlaege kalkulationId={kalkulationId} runId={m.runId} beschreibbar={beschreibbar} onGeaendert={onGeaendert} />}
      {m.actions?.map((a, i) =>
        a.type === 'OPEN_REQUESTS' ? (
          <Link key={i} to={`/calculations/material-requests?tab=material&calculationId=${kalkulationId}`} className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-xs hover:bg-accent">
            {a.label}
          </Link>
        ) : (
          <button key={i} type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs text-primary-foreground" onClick={() => onAktion(a)}>
            <Play className="h-3.5 w-3.5" /> Starten: {a.label}
          </button>
        ),
      )}
      {m.tokens !== undefined && (
        <div className="text-[11px] text-muted-foreground">
          {m.tokens.toLocaleString('de-DE')} Tokens{m.tools?.length ? ` · ${[...new Set(m.tools)].join(', ')}` : ''}
        </div>
      )}
    </div>
  )
}

/** `[Text](pos:<id>)` und `[Text](datei:<fileId>#<seite>)` aus der Antwort als Sprünge (8.2). */
function MitSpruengen({ text, onPosition, onFundstelle }: { text: string; onPosition: (id: string) => void; onFundstelle: (f: { fileId: string; seite: number }) => void }) {
  const teile: ReactNode[] = []
  const muster = /\[([^\]]+)\]\((pos|datei):([0-9a-f-]{36})(?:#(\d+))?\)/g
  let rest = 0
  for (const t of text.matchAll(muster)) {
    const [ganz, beschriftung, art, id, seite] = t
    teile.push(text.slice(rest, t.index))
    teile.push(
      <button
        key={t.index}
        type="button"
        className="text-primary hover:underline"
        onClick={() => (art === 'pos' ? onPosition(id) : onFundstelle({ fileId: id, seite: Number(seite ?? 1) }))}
      >
        {beschriftung}
      </button>,
    )
    rest = (t.index ?? 0) + ganz.length
  }
  teile.push(text.slice(rest))
  return <>{teile}</>
}
